import { useEffect, useState } from "react";
import { e164, tidyPhoneInput } from "../data/phone";
import type { SessionApi } from "../data/session";

/**
 * The three ways in, behind one button.
 *
 * The landing page sells with a single "Get started"; naming a provider on the
 * hero button asks the visitor to make an account decision before they have
 * decided they want the product at all. The choice belongs here, one step
 * later, where the only question left is "which of these is me".
 *
 * Phone is FIRST for India. It is the identity most households actually have,
 * and putting it third reads as an afterthought — see
 * docs/decisions/0016-three-ways-in.md.
 */
export function SignIn({ api, onClose }: { api: SessionApi; onClose: () => void }) {
  const [phone, setPhone] = useState("");
  // Autofill and paste both arrive as a full `+91…`; the field absorbs the code
  // into its label rather than showing it twice. A number from elsewhere keeps
  // its own code, and the label steps out of the way.
  const [ownCountryCode, setOwnCountryCode] = useState(false);
  const [code, setCode] = useState("");
  const { busy, error, awaitingCode } = api;

  // A ten-digit local number is the shortest thing worth sending a code to.
  const phoneReady = phone.replace(/\D/g, "").replace(/^0+/, "").length >= 10;

  // Escape closes it. The backdrop below covers the pointer; this covers the
  // keyboard, and between them nobody is trapped in here.
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  return (
    <div className="signin-scrim">
      {/* Tapping outside closes. A real button rather than a click handler on
          the backdrop div: the div version is invisible to a screen reader and
          unreachable by keyboard, which is how "tap anywhere to dismiss"
          quietly becomes "no way to dismiss". */}
      <button
        type="button"
        className="signin-backdrop"
        aria-label="Close sign-in"
        onClick={onClose}
      />
      <div
        className="signin"
        role="dialog"
        aria-modal="true"
        aria-label="Sign in"
        data-testid="signin-sheet"
      >
        {awaitingCode ? (
          <>
            <h2 className="signin-title">Enter the code</h2>
            {/* The full number, not what's left in the field — "sent to
                9163975637" gives no way to spot a wrong country code, which is
                exactly the mistake worth catching here. */}
            <p className="signin-note">Sent to {e164(phone)}.</p>
            <input
              className="signin-input"
              // A numeric keypad on a phone, and the OS offers the SMS code
              // above the keyboard instead of making anyone memorise it.
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="otp-input"
            />
            <button
              type="button"
              className="primary"
              disabled={busy || code.trim().length < 6}
              onClick={() => api.confirmPhoneCode(code)}
              data-testid="otp-confirm"
            >
              {busy ? "Checking…" : "Confirm"}
            </button>
            <button
              type="button"
              className="signin-link"
              onClick={() => {
                setCode("");
                api.cancelPhone();
              }}
            >
              Use a different number
            </button>
          </>
        ) : (
          <>
            <h2 className="signin-title">Get started</h2>

            <label className="signin-label" htmlFor="phone">
              Phone number
            </label>
            <div className="signin-phone">
              {!ownCountryCode && <span className="signin-prefix">+91</span>}
              <input
                id="phone"
                className="signin-input"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="98765 43210"
                value={phone}
                onChange={(e) => {
                  const tidy = tidyPhoneInput(e.target.value);
                  setPhone(tidy.value);
                  setOwnCountryCode(tidy.hasCountryCode);
                }}
                data-testid="phone-input"
              />
            </div>
            <button
              type="button"
              className="primary"
              disabled={busy || !phoneReady}
              onClick={() => api.sendPhoneCode(phone)}
              data-testid="phone-send"
            >
              {busy ? "One moment…" : "Send me a code"}
            </button>

            <p className="signin-or">or</p>

            <button
              type="button"
              className="provider"
              disabled={busy}
              onClick={api.signInWithGoogle}
              data-testid="sign-in-google"
            >
              Continue with Google
            </button>
            <button
              type="button"
              className="provider"
              disabled={busy}
              onClick={api.signInWithApple}
              data-testid="sign-in-apple"
            >
              Continue with Apple
            </button>
          </>
        )}

        {error && (
          <p className="hint" data-testid="auth-error">
            {error}
          </p>
        )}

        {/* Firebase mounts the invisible reCAPTCHA challenge here. It must exist
            in the DOM before the first code is sent, and must not be unmounted
            between attempts. */}
        <div id="recaptcha-holder" />

        <button type="button" className="signin-link" onClick={onClose}>
          Not now
        </button>
      </div>
    </div>
  );
}
